import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Euro, Calendar, User, Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency } from '../utils/format';

const STAGE_COLORS = {
  lead: { bg: '#6366F1', light: 'rgba(99,102,241,0.08)', text: '#6366F1' },
  qualified: { bg: '#3B82F6', light: 'rgba(59,130,246,0.08)', text: '#3B82F6' },
  proposal: { bg: '#FFAB00', light: 'rgba(255,171,0,0.08)', text: '#B07800' },
  negotiation: { bg: '#F97316', light: 'rgba(249,115,22,0.08)', text: '#F97316' },
  won: { bg: '#1B99E5', light: 'rgba(27,153,229,0.08)', text: '#1B99E5' },
  lost: { bg: '#EF4444', light: 'rgba(239,68,68,0.08)', text: '#EF4444' },
};

const formatValue = (v) => {
  if (!v) return '0 €';
  return formatCurrency(v);
};

export default function Pipeline() {
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [defaultStage, setDefaultStage] = useState('lead');
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dealForm, setDealForm] = useState({
    title: '', value: '', currency: 'EUR', stage: 'lead',
    contact_id: '', contact_name: '', company_id: '', company_name: '',
    probability: '10', expected_close_date: '', notes: '', tags: []
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [stagesRes, dealsRes, contactsRes, companiesRes] = await Promise.all([
        api.get('/pipeline/stages'),
        api.get('/deals'),
        api.get('/contacts'),
        api.get('/companies')
      ]);
      setStages(stagesRes.data);
      setDeals(dealsRes.data);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const dealsByStage = stages.reduce((acc, stage) => {
    acc[stage.id] = deals.filter(d => d.stage === stage.id);
    return acc;
  }, {});

  const stageTotals = stages.reduce((acc, stage) => {
    const stageDeals = deals.filter(d => d.stage === stage.id);
    acc[stage.id] = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
    return acc;
  }, {});

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageId) setDragOverStage(stageId);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedDeal || draggedDeal.stage === stageId) return;

    const prevDeals = [...deals];
    setDeals(prev => prev.map(d => d.id === draggedDeal.id ? { ...d, stage: stageId } : d));

    try {
      await api.put(`/deals/${draggedDeal.id}/stage`, { stage: stageId });
      const stageName = stages.find(s => s.id === stageId)?.name;
      toast.success(`Devis déplacé vers "${stageName}"`);
    } catch {
      setDeals(prevDeals);
      toast.error('Erreur lors du déplacement');
    }
    setDraggedDeal(null);
  };

  const openAddDeal = (stageId = 'lead') => {
    setDefaultStage(stageId);
    setDealForm({
      title: '', value: '', currency: 'EUR', stage: stageId,
      contact_id: '', contact_name: '', company_id: '', company_name: '',
      probability: '10', expected_close_date: '', notes: '', tags: []
    });
    setShowAddDeal(true);
  };

  const handleSaveDeal = async () => {
    if (!dealForm.title) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const res = await api.post('/deals', {
        ...dealForm,
        value: parseFloat(dealForm.value) || 0,
        probability: parseInt(dealForm.probability) || 10,
      });
      setDeals(prev => [res.data, ...prev]);
      setShowAddDeal(false);
      toast.success('Devis créé !');
    } catch {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      await api.delete(`/deals/${dealId}`);
      setDeals(prev => prev.filter(d => d.id !== dealId));
      toast.success('Devis supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pipeline de vente
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {deals.length} devis · Faites glisser pour déplacer
          </p>
        </div>
        <Button
          onClick={() => openAddDeal()}
          className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
          style={{ transition: 'background-color 150ms ease, transform 100ms ease' }}
          data-testid="add-deal-button"
        >
          <Plus size={15} className="mr-1.5" /> Nouveau devis
        </Button>
      </div>

      {/* Kanban Board */}
      <div className="kanban-board flex gap-3 flex-1 min-h-0">
        {stages.map(stage => {
          const stageDeals = dealsByStage[stage.id] || [];
          const colors = STAGE_COLORS[stage.id] || { bg: '#94a3b8', light: 'rgba(148,163,184,0.08)', text: '#64748b' };
          const isOver = dragOverStage === stage.id;

          return (
            <div
              key={stage.id}
              className={`flex-shrink-0 w-72 flex flex-col rounded-xl border-2 ${
                isOver ? 'border-[#1B99E5]' : 'border-transparent'
              }`}
              style={{ background: isOver ? 'rgba(27,153,229,0.03)' : 'transparent', transition: 'border-color 150ms ease, background 150ms ease' }}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              data-testid={`stage-column-${stage.id}`}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-3 py-2.5 mb-1 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{stage.name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-full px-1.5 py-0.5">
                    {stageDeals.length}
                  </span>
                </div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {stageTotals[stage.id] >= 1000 ? `${(stageTotals[stage.id]/1000).toFixed(0)}k €` : `${stageTotals[stage.id]} €`}
                </span>
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto px-1.5 space-y-2 min-h-[200px]">
                {stageDeals.map(deal => (
                  <div
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal)}
                    onDragEnd={handleDragEnd}
                    className="deal-card bg-white dark:bg-[#22262E] rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md group"
                    style={{ transition: 'box-shadow 150ms ease, transform 150ms ease' }}
                    data-testid={`deal-card-${deal.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4
                        className="text-sm font-semibold text-gray-900 dark:text-white leading-tight cursor-pointer hover:text-[#1B99E5] flex-1"
                        onClick={() => navigate(`/deals/${deal.id}`)}
                        style={{ transition: 'color 150ms ease' }}
                      >
                        {deal.title}
                      </h4>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                            style={{ transition: 'opacity 150ms ease' }}>
                            <MoreVertical size={14} className="text-gray-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/deals/${deal.id}`)}>
                            Voir le détail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteDeal(deal.id)} className="text-red-600 focus:text-red-600">
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {deal.value > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        <span className="text-sm font-bold" style={{ color: colors.text }}>
                          {formatValue(deal.value)}
                        </span>
                      </div>
                    )}

                    <div className="space-y-1">
                      {deal.company_name && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <Building2 size={11} />
                          <span className="truncate">{deal.company_name}</span>
                        </div>
                      )}
                      {deal.contact_name && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <User size={11} />
                          <span className="truncate">{deal.contact_name}</span>
                        </div>
                      )}
                      {deal.expected_close_date && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                          <Calendar size={11} />
                          <span>{deal.expected_close_date}</span>
                        </div>
                      )}
                    </div>

                    {deal.probability > 0 && (
                      <div className="mt-2.5">
                        <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${deal.probability}%`, backgroundColor: stage.color }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {stageDeals.length === 0 && (
                  <div className="h-24 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center">
                    <p className="text-xs text-gray-400 dark:text-gray-600">Déposez ici</p>
                  </div>
                )}
              </div>

              {/* Add button */}
              <div className="px-1.5 pt-2 pb-1.5 flex-shrink-0">
                <button
                  onClick={() => openAddDeal(stage.id)}
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-[#1A1D23] hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
                  style={{ transition: 'background-color 150ms ease, color 150ms ease' }}
                  data-testid={`add-deal-stage-${stage.id}`}
                >
                  <Plus size={13} /> Ajouter un devis
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Deal Dialog */}
      <Dialog open={showAddDeal} onOpenChange={setShowAddDeal}>
        <DialogContent className="sm:max-w-md" data-testid="add-deal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouveau devis</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Titre *</Label>
              <Input
                placeholder="Ex: Contrat SaaS - Acme Corp"
                value={dealForm.title}
                onChange={e => setDealForm({ ...dealForm, title: e.target.value })}
                data-testid="deal-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Valeur</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={dealForm.value}
                  onChange={e => setDealForm({ ...dealForm, value: e.target.value })}
                  data-testid="deal-value-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Étape</Label>
                <Select value={dealForm.stage} onValueChange={v => setDealForm({ ...dealForm, stage: v })}>
                  <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Contact</Label>
                <Select value={dealForm.contact_id} onValueChange={v => {
                  const c = contacts.find(c => c.id === v);
                  setDealForm({ ...dealForm, contact_id: v, contact_name: c ? `${c.first_name} ${c.last_name}` : '' });
                }}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Entreprise</Label>
                <Select value={dealForm.company_id} onValueChange={v => {
                  const c = companies.find(c => c.id === v);
                  setDealForm({ ...dealForm, company_id: v, company_name: c ? c.name : '' });
                }}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Date de clôture</Label>
                <Input
                  type="date"
                  value={dealForm.expected_close_date}
                  onChange={e => setDealForm({ ...dealForm, expected_close_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Probabilité (%)</Label>
                <Input
                  type="number"
                  min="0" max="100"
                  value={dealForm.probability}
                  onChange={e => setDealForm({ ...dealForm, probability: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Notes</Label>
              <Textarea
                placeholder="Notes sur ce deal..."
                value={dealForm.notes}
                onChange={e => setDealForm({ ...dealForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDeal(false)}>Annuler</Button>
            <Button
              onClick={handleSaveDeal}
              disabled={saving}
              className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white"
              data-testid="save-deal-button"
            >
              {saving ? 'Création...' : 'Créer le devis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
